import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { FilterIcon, Loader2 } from "lucide-react";
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";
import { useEffect, useState } from "react";
import { Label } from "./ui/label";
import CustomLabelText from "./CustomLabelText";
import { OrganizationTypeSelectMulti } from "./OrganizationTypeSelectMulti";
import { VersionSelectMulti } from "./VersionSelectMulti";
import SearchSelect from "./SearchSelect";
import { sectionTypeOptions } from "../config";

// 1. Local state to track filter values
let _organizationTypeId, _versionId;

// 2. Exported function to get current state
export function getSelectedFilters() {
  return {
    organizationTypeId: _organizationTypeId,
    versionId: _versionId,
  };
}

const getCount = (val) => {
  if (!val) return 0;
  if (Array.isArray(val)) return val.length;
  return 1; // single selected value
};

const commonList = [
  {
    label: "Yes",
    value: "1",
  },
  {
    label: "No",
    value: "0",
  },
];

export default function AdminFilters({
  organizationType = false,
  organizationTypeId = "",
  setOrganizationTypeId,
  version = false,
  versionId = "",
  setVersionId,
  status = false,
  statusId = "",
  setStatusId,
  sectionTypeSelect = false,
  sectionType = "",
  setSectionType
}) {
  const [organizationTypeLoading, setOrganizationTypeLoading] = useState(false);
  const [organizationTypeListing, setOrganizationTypeListing] = useState([]);
  const [organizationTypeList, setOrganizationTypeList] = useState([]);

  const [versionLoading, setVersionLoading] = useState(false);
  const [versionListing, setVersionListing] = useState([]);
  const [versionList, setVersionList] = useState([]);

  // Sync values to exported variables
  _organizationTypeId = organizationTypeId;
  _versionId = versionId;

  useEffect(() => {
    if (organizationTypeList) {
      let organizationTypeTempListing = organizationTypeList?.map((item) => ({
        ...item,
        value: item?.id,
        label: item?.name ? item?.name : "",
      }));

      setOrganizationTypeListing(organizationTypeTempListing || []);
    } else {
      setOrganizationTypeListing([]);
    }
  }, [organizationTypeList]);

  useEffect(() => {
    if (versionList) {
      let versionTempListing = versionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: item?.version_number
          ? `${item?.version_number} - ${item?.name}`
          : "",
      }));

      setVersionListing(versionTempListing || []);
    } else {
      setVersionListing([]);
    }
  }, [versionList]);

  const totalSelectedCount =
    getCount(organizationTypeId) +
    getCount(statusId) +
    getCount(sectionType) +
    (getCount(versionId) ? 1 : 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="relative px-2">
          <FilterIcon className="h-4 w-4" />
          {totalSelectedCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-semibold text-secondary">
              {totalSelectedCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[250px]">
        {organizationType && (
          <DropdownMenuItem
            className="focus:bg-transparent block"
            asChild
            onSelect={(e) => e.preventDefault()}
          >
            <div
            // onPointerDown={(e) => e.stopPropagation()}
            // onClick={(e) => e.preventDefault()}
            >
              <div className="grid gap-2">
                <div className="min-w-0">
                  <Label htmlFor="contact_id">
                    <CustomLabelText
                      labelText="Industry Type"
                      errorField={""}
                      className={"mb-2"}
                    />
                  </Label>
                  <div className="relative">
                    <OrganizationTypeSelectMulti
                      options={organizationTypeListing?.filter(
                        (item) => item?.value !== "new"
                      )}
                      placeholder={"Select Types"}
                      onValueChange={(e) => {
                        setOrganizationTypeId(e);
                      }}
                      variant="inverted"
                      animation={2}
                      maxCount={0}
                      defaultValue={
                        organizationTypeId ? organizationTypeId : ""
                      }
                      setOrganizationTypeList={setOrganizationTypeList} // Update vendor list as new data loads
                      setOrganizationTypeLoading={setOrganizationTypeLoading}
                      loading={organizationTypeLoading}
                    />
                    {organizationTypeLoading && (
                      <span className="absolute top-2 right-2 animate-spin">
                        <Loader2 width={20} height={20} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        )}
        {version && (
          <DropdownMenuItem
            className="focus:bg-transparent block"
            asChild
            onSelect={(e) => e.preventDefault()}
          >
            <div>
              <div className="grid gap-2">
                <div className="min-w-0">
                  <Label htmlFor="contact_id">
                    <CustomLabelText
                      labelText="Version Number"
                      errorField={""}
                      className={"mb-2"}
                    />
                  </Label>
                  <div className="relative">
                    <VersionSelectMulti
                      options={versionListing?.filter(
                        (item) => item?.value !== "new"
                      )}
                      placeholder={"Select Number"}
                      onValueChange={(e) => {
                        setVersionId(e);
                      }}
                      variant="inverted"
                      animation={2}
                      maxCount={0}
                      defaultValue={versionId ? versionId : ""}
                      setVersionList={setVersionList} // Update vendor list as new data loads
                      setVersionLoading={setVersionLoading}
                      loading={versionLoading}
                    />
                    {versionLoading && (
                      <span className="absolute top-2 right-2 animate-spin">
                        <Loader2 width={20} height={20} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        )}
        {sectionTypeSelect && (
          <DropdownMenuItem
            className="focus:bg-transparent block"
            asChild
            onSelect={(e) => e.preventDefault()}
          >
            <div>
              <div className="grid gap-2">
                    <Label htmlFor="role_id">
                      <CustomLabelText
                        required={true}
                        labelText="Section Type"
                        errorField={""}
                        bottomAlign={true}
                      />
                    </Label>

                    <div className="relative">
                      <SearchSelect
                        frameworks={sectionTypeOptions}
                        optionType={"Section Type"}
                        handleSelect={(e) => {
                          setSectionType(e);
                        }}
                        clearable
                        onClear={() => {
                          setSectionType("")
                        }}
                        value={sectionType}
                        disabled={false}
                        initialValue={sectionType}
                      />
                    </div>
                  </div>
            </div>
          </DropdownMenuItem>
        )}
        {status && (
          <DropdownMenuItem
            className="focus:bg-transparent block"
            asChild
            onSelect={(e) => e.preventDefault()}
          >
            <div
            // onPointerDown={(e) => e.stopPropagation()}
            // onClick={(e) => e.preventDefault()}
            >
              <div className="grid gap-2">
                <div className="min-w-0">
                  <Label htmlFor="aml_ctf_contact_id">
                    <CustomLabelText
                      labelText="Status"
                      errorField={""}
                      className={"mb-2"}
                    />
                  </Label>
                  <div className="relative">
                    <SearchSelect
                      frameworks={commonList}
                      optionType={"Status"}
                      handleSelect={(e) => {
                        setStatusId(e);
                      }}
                      disabled={false}
                      clearable={true}
                      initialValue={statusId ? statusId : ""}
                      onClear={() => {
                        setStatusId();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        )}
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();

              status && setStatusId();

              if (organizationType) {
                setOrganizationTypeId("");
              }
            }}
            className="text-red-500 focus:bg-destructive/10"
          >
            Reset
          </DropdownMenuItem>
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
